<template>
  <AuthLayout>
    <div>
      <h1 class="text-2xl font-bold text-gray-900">Set new password</h1>
      <p class="text-sm text-gray-400 mt-1">Choose a strong password for your account</p>
    </div>

    <!-- Invalid token -->
    <div v-if="tokenError" class="mt-5 p-4 rounded-lg bg-red-50 border border-red-100">
      <div class="flex items-start gap-2">
        <svg class="w-5 h-5 text-red-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <div>
          <p class="text-sm font-medium text-red-700">Invalid Reset Link</p>
          <p class="text-sm text-red-600 mt-0.5">{{ tokenError }}</p>
        </div>
      </div>
      <router-link to="/forgot-password" class="inline-flex items-center gap-1.5 mt-3 text-sm text-[var(--color-primary)] hover:underline">
        Request a new reset link
      </router-link>
    </div>

    <!-- Success -->
    <div v-else-if="success" class="mt-8">
      <div class="p-4 rounded-lg bg-green-50 border border-green-100">
        <div class="flex items-start gap-2">
          <svg class="w-5 h-5 text-green-500 mt-0.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <div>
            <p class="text-sm font-medium text-green-700">Password updated</p>
            <p class="text-sm text-green-600 mt-0.5">Your password has been successfully reset.</p>
          </div>
        </div>
      </div>
      <router-link to="/login" class="mt-6 w-full h-11 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors flex items-center justify-center">
        Sign in with new password
      </router-link>
    </div>

    <!-- Loading -->
    <div v-else-if="validating" class="mt-8 text-center py-8">
      <div class="loading-spinner mx-auto mb-3" />
      <p class="text-sm text-gray-400">Validating reset link...</p>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="handleReset" class="mt-8">
      <p v-if="email" class="text-sm text-gray-500 mb-5">Resetting password for <strong>{{ email }}</strong></p>

      <div class="space-y-5">
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">New Password</label>
          <PasswordInput v-model="password" placeholder="Enter new password" :required="true" />
          <div class="mt-3">
            <div class="flex gap-1.5">
              <div v-for="i in 4" :key="i" class="h-1.5 flex-1 rounded-full transition-all duration-300" :class="i <= strengthLevel ? strengthBarColor : 'bg-gray-100'" />
            </div>
            <p class="text-xs mt-1.5" :class="strengthColor">{{ strengthLabel }}</p>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Confirm Password</label>
          <PasswordInput v-model="confirmPassword" placeholder="Confirm new password" :required="true" />
          <p v-if="password && confirmPassword && password !== confirmPassword" class="text-xs text-red-500 mt-1.5">Passwords do not match</p>
        </div>
      </div>

      <button type="submit" :disabled="loading || !isValid" class="mt-6 w-full h-11 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {{ loading ? 'Resetting...' : 'Reset Password' }}
      </button>
    </form>
  </AuthLayout>
</template>

<script>
import AuthLayout from './AuthLayout.vue'
import PasswordInput from '../../components/PasswordInput.vue'
import api from '../../services/api.js'
import { notifyError } from '../../services/notifications.js'

export default {
  name: 'ResetPassword',
  components: { AuthLayout, PasswordInput },

  data() {
    return {
      token: null,
      email: '',
      password: '',
      confirmPassword: '',
      loading: false,
      validating: true,
      tokenError: null,
      success: false
    }
  },

  computed: {
    isValid() {
      return this.password && this.password.length >= 8 && this.password === this.confirmPassword
    },
    strengthLevel() {
      const p = this.password
      if (!p) return 0
      let s = 0
      if (p.length >= 8) s++
      if (/[A-Z]/.test(p)) s++
      if (/[0-9]/.test(p)) s++
      if (/[^A-Za-z0-9]/.test(p)) s++
      return s
    },
    strengthLabel() {
      return ['', 'Weak', 'Fair', 'Good', 'Strong'][this.strengthLevel] || ''
    },
    strengthColor() {
      return ['text-gray-400', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-500'][this.strengthLevel] || 'text-gray-400'
    },
    strengthBarColor() {
      return ['bg-gray-200', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-400'][this.strengthLevel] || 'bg-gray-200'
    }
  },

  async created() {
    this.token = this.$route.params.token
    try {
      const res = await api.get(`/auth/reset-password/${this.token}`)
      this.email = res.data?.email || ''
    } catch (err) {
      this.tokenError = err?.message || 'This reset link is invalid or has expired.'
    } finally {
      this.validating = false
    }
  },

  methods: {
    async handleReset() {
      if (!this.isValid) return
      this.loading = true
      try {
        await api.post(`/auth/reset-password/${this.token}`, { password: this.password })
        this.success = true
      } catch (err) {
        notifyError(err?.message || 'Failed to reset password')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
